import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import BoxList from 'modules/core/components/BoxList'
import InlineSearch from 'modules/core/components/InlineSearch'
import ScrollableList from 'modules/core/components/ScrollableList'
import { useEffect } from 'modules/core/hooks/useEffect'
import useSearch from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

import type { CommunitiesDropdown } from 'modules/dashboard/components/Groups/types'
import groups from 'modules/dashboard/store/collections/communities/Groups'
import type Group from 'modules/dashboard/store/models/communities/Group'

import GroupsList from './GroupList'

interface Props {
  selectedGroup: CommunitiesDropdown | null | undefined
  onSelect: React.Dispatch<CommunitiesDropdown>
  createGroup: () => void
  joinGroup: () => void
  excludeCelebrationGroups?: boolean
  canCreateGroup?: boolean
}

const GroupSelector = observer(
  ({
    selectedGroup,
    createGroup,
    onSelect,
    joinGroup,
    excludeCelebrationGroups,
    canCreateGroup,
  }: Props) => {
    const { search, onSearch } = useSearch()
    const [trackedSearchEvent, setTrackedSearchEvent] = React.useState(false)

    useEffect(() => {
      if (search.length && !trackedSearchEvent) {
        setTrackedSearchEvent(true)
      }
    }, [search])

    const groupsDropdown = (): CommunitiesDropdown[] => {
      const currentUserGroups = groups.fromCurrentUser
      return currentUserGroups
        .filter((group) => {
          return group.get('identifier') !== 'celebrations'
        })
        .map((group: Group) => ({
          title: group.get('title'),
          strategy: 'group',
          value: group.get('id'),
        }))
    }

    const dropdownOptions = (): CommunitiesDropdown[] => {
      const all_communities: CommunitiesDropdown[] = [
        { title: i18n.t('posts.all_communities.title'), strategy: 'all' },
      ]
      const types: CommunitiesDropdown[] = [
        { title: i18n.t('posts.kudos.title'), strategy: 'type', value: 'Kudo' },
      ]

      if (!excludeCelebrationGroups) {
        types.unshift(
          { title: i18n.t('posts.birthdays.title'), strategy: 'type' as const, value: 'Birthday' },
          {
            title: i18n.t('posts.new_joiners.title'),
            strategy: 'type' as const,
            value: 'FirstDay',
          },
          {
            title: i18n.t('posts.workiversaries.title'),
            strategy: 'type' as const,
            value: 'Workiversary',
          }
        )
      }

      const groups = groupsDropdown()

      return [...all_communities, ...groups, ...types]
    }

    const filteredOptions = dropdownOptions().filter(
      (option: CommunitiesDropdown) => !search || matchText(search, option.title)
    )

    return (
      <Box gap="s16">
        <BoxList>
          <InlineSearch onSearch={onSearch} search={search} />
          <ScrollableList>
            <GroupsList
              selectedGroup={selectedGroup}
              options={filteredOptions}
              onSelect={onSelect}
            />
          </ScrollableList>
        </BoxList>
        <Box flexDirection="row" justifyContent="flexStart" gap="s8">
          {canCreateGroup && (
            <Button
              type="secondary"
              onClick={() => {
                createGroup()
              }}
              iconName="add"
            >
              {i18n.t('dashboard.groups.create_group')}
            </Button>
          )}
          <Button
            type="secondary"
            onClick={() => {
              joinGroup()
            }}
            iconName="search"
          />
        </Box>
      </Box>
    )
  }
)

export default GroupSelector
